import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Car, MapPin, Phone, Star, Navigation, Clock, CheckCircle, Loader2, FileText } from 'lucide-react';

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons
const carIcon = new L.DivIcon({
  className: 'custom-car-icon',
  html: '<div style="background: #f59e0b; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"><svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg></div>',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const pickupIcon = new L.DivIcon({
  className: 'custom-pickup-icon',
  html: '<div style="background: #22c55e; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);"><svg width="14" height="14" viewBox="0 0 24 24" fill="white"><circle cx="12" cy="12" r="8"/></svg></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const dropoffIcon = new L.DivIcon({
  className: 'custom-dropoff-icon',
  html: '<div style="background: #ef4444; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);"><svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 24],
});

// Map bounds updater component
function MapBoundsUpdater({ positions }) {
  const map = useMap();
  
  useEffect(() => {
    if (positions && positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, positions]);
  
  return null;
}

const RideTracker = ({ ride, driver, onRefresh }) => {
  const [driverLocation, setDriverLocation] = useState(null);
  
  useEffect(() => {
    if (driver?.current_location) {
      setDriverLocation(driver.current_location);
    }
  }, [driver]);

  const getStatusInfo = (status) => {
    const statuses = {
      'searching_driver': { label: 'Finding your driver...', color: 'text-blue-600', bg: 'bg-blue-100' },
      'driver_assigned': { label: 'Driver on the way', color: 'text-purple-600', bg: 'bg-purple-100' },
      'arrived_at_pickup': { label: 'Driver has arrived', color: 'text-green-600', bg: 'bg-green-100' },
      'in_progress': { label: 'Ride in progress', color: 'text-cyan-600', bg: 'bg-cyan-100' },
      'completed': { label: 'Ride completed', color: 'text-green-600', bg: 'bg-green-100' },
    };
    return statuses[status] || { label: status, color: 'text-gray-600', bg: 'bg-gray-100' };
  };

  if (!ride) {
    return (
      <Card className="p-8 text-center">
        <Car size={48} className="mx-auto mb-4 text-gray-400" />
        <p className="text-gray-500">No active ride</p>
      </Card>
    );
  }

  const pickupCoords = ride.pickup ? [ride.pickup.lat, ride.pickup.lng] : null;
  const dropoffCoords = ride.dropoff ? [ride.dropoff.lat, ride.dropoff.lng] : null;
  const driverCoords = driverLocation ? [driverLocation.lat, driverLocation.lng] : null;

  // Get all positions for bounds
  const allPositions = [pickupCoords, dropoffCoords, driverCoords].filter(Boolean);
  
  // Default center (if no coordinates)
  const defaultCenter = pickupCoords || [26.1224, -80.1373]; // Fort Lauderdale

  const statusInfo = getStatusInfo(ride.status);

  return (
    <div className="space-y-4">
      {/* Status Bar */}
      <Card className={`p-4 ${statusInfo.bg}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {ride.status === 'searching_driver' ? (
              <Loader2 className={`${statusInfo.color} animate-spin`} size={24} />
            ) : ride.status === 'completed' ? (
              <CheckCircle className={statusInfo.color} size={24} />
            ) : (
              <Navigation className={statusInfo.color} size={24} />
            )}
            <div>
              <p className={`font-semibold ${statusInfo.color}`}>{statusInfo.label}</p>
              <p className="text-sm text-gray-600">
                {ride.status === 'driver_assigned' && driver && `${driver.name} is on the way`}
                {ride.status === 'arrived_at_pickup' && 'Your driver is waiting'}
                {ride.status === 'in_progress' && 'Heading to destination'}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            Refresh
          </Button>
        </div>
      </Card>

      {/* Map */}
      <Card className="overflow-hidden rounded-xl">
        <div style={{ height: '350px', width: '100%' }}>
          <MapContainer
            center={defaultCenter}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {/* Pickup Marker */}
            {pickupCoords && (
              <Marker position={pickupCoords} icon={pickupIcon}>
                <Popup>
                  <strong>Pickup</strong><br/>
                  {ride.pickup.address}
                </Popup>
              </Marker>
            )}
            
            {/* Dropoff Marker */}
            {dropoffCoords && (
              <Marker position={dropoffCoords} icon={dropoffIcon}>
                <Popup>
                  <strong>Dropoff</strong><br/>
                  {ride.dropoff.address}
                </Popup>
              </Marker>
            )}
            
            {/* Driver Marker */}
            {driverCoords && driver && (
              <Marker position={driverCoords} icon={carIcon}>
                <Popup>
                  <strong>{driver.name}</strong><br/>
                  {driver.vehicle_model}<br/>
                  {driver.license_plate}
                </Popup>
              </Marker>
            )}
            
            {/* Route line */}
            {pickupCoords && dropoffCoords && (
              <Polyline
                positions={[pickupCoords, dropoffCoords]}
                color="#6366f1"
                weight={3}
                dashArray="10, 10"
              />
            )}
            
            {/* Driver to pickup line */}
            {driverCoords && pickupCoords && ride.status === 'driver_assigned' && (
              <Polyline
                positions={[driverCoords, pickupCoords]}
                color="#f59e0b"
                weight={3}
              />
            )}
            
            <MapBoundsUpdater positions={allPositions} />
          </MapContainer>
        </div>
      </Card>

      {/* Driver Info Card */}
      {driver && ride.status !== 'searching_driver' && (
        <Card className="p-4 border-2" data-testid="driver-info-card">
          <div className="flex items-center gap-4">
            {/* Driver Profile Photo */}
            {driver.profile_photo ? (
              <img 
                src={driver.profile_photo.startsWith('http') ? driver.profile_photo : `${process.env.REACT_APP_BACKEND_URL}${driver.profile_photo}`}
                alt={driver.name}
                className="w-14 h-14 rounded-full object-cover flex-shrink-0 border-2 border-amber-400"
                data-testid="driver-profile-photo"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className={`w-14 h-14 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full items-center justify-center flex-shrink-0 ${driver.profile_photo ? 'hidden' : 'flex'}`}
              data-testid="driver-default-avatar"
            >
              <Car className="text-white" size={28} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-bold text-lg" data-testid="driver-name">{driver.name}</h3>
                <div className="flex items-center gap-1 text-sm">
                  <Star size={14} className="fill-amber-400 text-amber-400" />
                  <span>{driver.rating || '4.9'}</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm">{driver.vehicle_model} • {driver.license_plate}</p>
            </div>
            {/* Chat button instead of phone - privacy protected */}
            <div className="text-xs text-gray-400 italic">
              Use in-app chat to contact driver
            </div>
          </div>
          
          {/* Vehicle Photos Preview */}
          {(driver.vehicle_front_photo || driver.vehicle_rear_photo) && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-gray-500 mb-2">YOUR VEHICLE</p>
              <div className="flex gap-2">
                {driver.vehicle_front_photo && (
                  <img 
                    src={driver.vehicle_front_photo.startsWith('http') ? driver.vehicle_front_photo : `${process.env.REACT_APP_BACKEND_URL}${driver.vehicle_front_photo}`}
                    alt="Vehicle Front"
                    className="w-20 h-14 rounded-lg object-cover border"
                    data-testid="vehicle-front-photo"
                  />
                )}
                {driver.vehicle_rear_photo && (
                  <img 
                    src={driver.vehicle_rear_photo.startsWith('http') ? driver.vehicle_rear_photo : `${process.env.REACT_APP_BACKEND_URL}${driver.vehicle_rear_photo}`}
                    alt="Vehicle Rear"
                    className="w-20 h-14 rounded-lg object-cover border"
                    data-testid="vehicle-rear-photo"
                  />
                )}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Ride Details */}
      <Card className="p-4 border-2">
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
            </div>
            <div>
              <p className="text-xs text-gray-500">PICKUP</p>
              <p className="font-medium">{ride.pickup?.address || 'Not set'}</p>
            </div>
          </div>
          
          {/* Display stops if any */}
          {ride.stops && ride.stops.length > 0 && (
            <div className="ml-3 border-l-2 border-dashed border-amber-300 pl-6 space-y-3">
              {ride.stops.map((stop, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-amber-700">{idx + 1}</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">STOP {idx + 1}</p>
                    <p className="font-medium text-gray-700">{stop.address}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <MapPin size={12} className="text-red-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">DROPOFF</p>
              <p className="font-medium">{ride.dropoff?.address || 'Not set'}</p>
            </div>
          </div>
        </div>
        
        {/* Special Instructions */}
        {ride.special_instructions && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <FileText size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-blue-700 uppercase">Customer Instructions</p>
                <p className="text-sm text-blue-800 mt-1">{ride.special_instructions}</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="mt-4 pt-4 border-t flex justify-between items-center">
          <div>
            <p className="text-xs text-gray-500">ESTIMATED FARE</p>
            <p className="text-xl font-bold text-green-600">${ride.estimated_price || 0}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">VEHICLE</p>
            <p className="font-medium capitalize">{ride.vehicle_type || 'sedan'}</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default RideTracker;
